import React, { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  TextField,
  FormHelperText,
  Typography,
  MenuItem,
  Box,
  makeStyles,
} from '@material-ui/core';
import PropTypes from 'prop-types';

import PasswordTextField from '../atoms/PasswordTextField';
import CheckBoxTermsOfUse from '../molecules/CheckBoxTermsOfUse';
import MaskedTextField from '../molecules/MaskedTextField';
import AutocompleteCityUF from './AutocompleteCityUF';
import { SegmentOptions } from '../functions/Options';
import { CreateAccountOpenCompany } from '../functions/Services';
import {
  EmailValidation,
  LastNameValidation,
  LengthValidation,
  NameValidation,
  ObjectValidation,
  PasswordValidation,
  TermsOfUseValidation,
  PhoneValidation,
} from '../functions/FormValidation';
import TextFieldStatus from '../atoms/TextFieldStatus';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',

    '& .MuiStepper-root': {
      padding: '20px 0 0',
    },

    '& .MuiFormControl-root': {
      width: '70%',

      '@media screen and (max-width: 959px)': {
        width: '100%',
      },
    },

    '& .MuiFormHelperText-root': {
      color: theme.palette.error.main,
      marginBottom: '20px',
    },
  },
  inputWrap: {
    display: 'grid',
    marginBottom: '30px',
    gap: '15px',
  },
  submitButtons: {
    display: 'flex',
  },
}));

const renderStepLabel = (text) => (
  <StepLabel>
    <Typography variant="body1">{text}</Typography>
  </StepLabel>
);

const OpenCompanyFormDrawer = ({
  data, setData, isLoading, setIsLoading,
}) => {
  const classes = useStyles();
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const name = 'Abrir Empresa | Lead Drawer';

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateFields = () => (
    ObjectValidation(data, 8)
    && LengthValidation(data)
    && NameValidation(data)
    && LastNameValidation(data)
    && PhoneValidation(data)
    && EmailValidation(data)
    && PasswordValidation(data)

  );

  const isFormValid = () => {
    try {
      return validateFields();
    } catch (e) {
      return false;
    }
  };

  const validateForm = () => {
    try {
      if (validateFields()) {
        setError({});
      }
    } catch (e) {
      setError({ field: e.fileName, message: e.message });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setIsLoading(true);

      CreateAccountOpenCompany(data, name);
    }
  };

  const changeData = (value, field) => {
    setData({ ...data, [field]: value });
    setError({});
  };

  const handleCityUFChange = (value) => {
    if (value) {
      setData({
        ...data,
        state: value.stateInitial,
        city: value.code,
        cityName: value.name,
      });
    } else {
      setData({
        ...data,
        state: null,
        city: null,
        cityName: null,
      });
    }
    setError({});
  };

  const renderButtons = () => (
    <div>
      <Button
        color="primary"
        onClick={handleBack}
        disabled={activeStep === 0 || isLoading}
      >
        Voltar
      </Button>
      <Button
        color="primary"
        variant="contained"
        onClick={handleNext}
        disabled={isLoading}
      >
        Avançar
      </Button>
    </div>
  );

  return (
    <form
      action={`${process.env.GATSBY_ABRIR_EMPRESA_BASE_URL}/signup`}
      method="post"
      name={name}
      className={classes.root}
    >
      <Typography variant="body1">Abrir empresa na Conube é rápido e online.</Typography>

      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          {renderStepLabel('Informações pessoais')}

          <StepContent>
            <Box className={classes.inputWrap}>
              <TextField
                label="Nome"
                name="firstname"
                value={data.firstname}
                onChange={(e) => changeData(e.target.value, 'firstname')}
                error={error.field === 'firstname'}
                disabled={isLoading}
              />

              <TextField
                label="Sobrenome"
                name="lastName"
                value={data.lastName}
                onChange={(e) => changeData(e.target.value, 'lastName')}
                error={error.field === 'lastName'}
                disabled={isLoading}
              />

              <MaskedTextField
                onChange={(e) => changeData(e.target.value, 'phone')}
                mask="+55 (99) 99999-9999"
                textFieldProps={{
                  label: 'Telefone',
                  name: 'phone',
                  type: 'tel',
                  value: data.phone,
                  floating: true,
                  error: error.field === 'phone',
                }}
                InputProps={{
                  disabled: isLoading,
                }}
              />
            </Box>
            {renderButtons()}
          </StepContent>
        </Step>

        <Step>
          {renderStepLabel('Como será sua nova empresa?')}

          <StepContent>
            <Box className={classes.inputWrap}>
              <TextField
                label="Qual segmento de atuação?"
                name="segmento_atuacao"
                value={data.segment}
                onChange={(e) => changeData(e.target.value, 'segment')}
                disabled={isLoading}
                select
              >
                {SegmentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <AutocompleteCityUF
                onChange={(value) => handleCityUFChange(value)}
                value={{
                  stateInitial: data.state,
                  code: data.city,
                  name: data.cityName,
                }}
                InputProps={{
                  endAdornment: (
                    <TextFieldStatus
                      error={Boolean(error.city)}
                      value={data.city && !error.city}
                    />
                  ),
                }}
                disabled={isLoading}
              />
            </Box>

            {renderButtons()}
          </StepContent>
        </Step>

        <Step>
          {renderStepLabel('Cadastro')}

          <StepContent>
            <Box className={classes.inputWrap}>
              <TextField
                label="E-mail"
                name="email"
                value={data.email}
                onChange={(e) => changeData(e.target.value, 'email')}
                error={error.field === 'email'}
                disabled={isLoading}
              />

              <PasswordTextField
                label="Senha"
                name="password"
                value={data.password}
                onChange={(e) => changeData(e.target.value, 'password')}
                error={error.field === 'password'}
                disabled={isLoading}
              />

            </Box>

            <FormHelperText>{error.message}</FormHelperText>

            <div className={classes.submitButtons}>
              <Button
                color="primary"
                onClick={handleBack}
                disabled={activeStep === 0 || isLoading}
              >
                Voltar
              </Button>
            </div>
          </StepContent>
        </Step>
      </Stepper>
    </form>
  );
};

OpenCompanyFormDrawer.propTypes = {
  data: PropTypes.shape().isRequired,
  setData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default OpenCompanyFormDrawer;
